/**
 * Yüklenecek dosyaların listesi. Her girdi Resources.items.<name> olarak erişilir;
 * type: 'texture' olanlar ayrıca <name>Texture adıyla THREE.Texture olarak da gelir.
 *
 * Eksik bir dosya hata vermez, yükleme ekranı sonsuza dek bekler —
 * yeni dosya eklerken `npm run check:assets` çalıştır.
 */
import palette from './palette.js'
import site from './site.js'
import glyphs from './glyphs.js'
import vehicles from './vehicles.js'

// Giriş başlığı parçaları (tools/blender/title.py ile aynı sıra)
export const TITLE_KEYS = ['bar1', 'bar2', 'p', 'a', 'u', 's', 'e']

// TAIGA harfleri (tools/blender/taiga.py ile aynı sıra)
export const TAIGA_KEYS = ['t', 'a1', 'i', 'g', 'a2']

// Dünyada 3D harflerle yazılan metinler → yalnızca kullanılan glifler yüklenir
export const BLOCK_TEXTS = [
    ...site.projects.map((_project) => _project.label).filter(Boolean),
    site.taiga.subtitle,
    ...site.installation.title
]

// Harf setinde olmayan karakter de listeye girer (kod noktasıyla) → dosyası olmadığı için check:assets yakalar
const glyphCode = (_char) => glyphs.chars[_char] ? glyphs.chars[_char].code : _char.codePointAt(0).toString(16).toUpperCase().padStart(4, '0')
export const GLYPH_CODES = [...new Set(BLOCK_TEXTS.join('').replace(/\s/g, '').split('').map(glyphCode))].sort()

const capitalize = (_value) => `${_value.substring(0, 1).toUpperCase()}${_value.substring(1)}`

export default [
    // Matcaps (tools/blender/matcaps.py)
    ...palette.matcaps.map((_name) => ({ name: `matcap${capitalize(_name)}`, source: `./models/matcaps/${_name}.png`, type: 'texture' })),

    // Giriş başlığı (tools/blender/title.py)
    ...TITLE_KEYS.flatMap((_key) => [
        { name: `introTitle${capitalize(_key)}Base`, source: `./models/intro/title/${_key}/base.glb` },
        { name: `introTitle${capitalize(_key)}Collision`, source: `./models/intro/title/${_key}/collision.glb` }
    ]),

    // TAIGA harfleri (tools/blender/taiga.py)
    ...TAIGA_KEYS.flatMap((_key) => [
        { name: `taiga${capitalize(_key)}Base`, source: `./models/taiga/${_key}/base.glb` },
        { name: `taiga${capitalize(_key)}Collision`, source: `./models/taiga/${_key}/collision.glb` }
    ]),

    // Teknoloji dekorları (tools/blender/decor.py); sabit olanların zemin gölgesi var
    ...['phone', 'mouse', 'glasses', 'braceOpen', 'braceClose', 'codeLt', 'codeSlash', 'codeGt', 'keycap', 'console', 'gamepad', 'treeRound', 'treeBlossom', 'treePine'].flatMap((_model) => [
        { name: `decor${capitalize(_model)}Base`, source: `./models/decor/${_model}/base.glb` },
        { name: `decor${capitalize(_model)}Collision`, source: `./models/decor/${_model}/collision.glb` },
        ...(['phone', 'console', 'treeRound', 'treeBlossom', 'treePine'].includes(_model) ? [{ name: `decor${capitalize(_model)}FloorShadow`, source: `./models/decor/${_model}/floorShadow.png`, type: 'texture' }] : [])
    ]),

    // Oyuncaklar (tools/blender/toys.py)
    ...['domino', 'cube', 'ball', 'cubelet'].flatMap((_toy) => [
        { name: `toys${capitalize(_toy)}Base`, source: `./models/toys/${_toy}/base.glb` },
        { name: `toys${capitalize(_toy)}Collision`, source: `./models/toys/${_toy}/collision.glb` }
    ]),

    // Garaj binası (tools/blender/garage.py)
    // Çeşme (tools/blender/fountain.py) — damlalar FountainSection.js'te
    { name: 'fountainStaticBase', source: './models/fountain/static/base.glb' },
    { name: 'fountainStaticCollision', source: './models/fountain/static/collision.glb' },
    { name: 'fountainStaticFloorShadow', source: './models/fountain/static/floorShadow.png', type: 'texture' },

    // Simge kuleler (tools/blender/galata.py, pisa.py)
    ...['galata', 'pisa'].flatMap((_name) => [
        { name: `${_name}StaticBase`, source: `./models/${_name}/static/base.glb` },
        { name: `${_name}StaticCollision`, source: `./models/${_name}/static/collision.glb` },
        { name: `${_name}StaticFloorShadow`, source: `./models/${_name}/static/floorShadow.png`, type: 'texture' }
    ]),

    // Heykel (tools/blender/statue.py)
    { name: 'statueStaticBase', source: './models/statue/static/base.glb' },
    { name: 'statueStaticCollision', source: './models/statue/static/collision.glb' },
    { name: 'statueStaticFloorShadow', source: './models/statue/static/floorShadow.png', type: 'texture' },

    { name: 'garageBase', source: './models/garage/base.glb' },
    { name: 'garageCollision', source: './models/garage/collision.glb' },
    { name: 'garageFloorShadow', source: './models/garage/floorShadow.png', type: 'texture' },

    // Garaj araçları (tools/blender/vehicles.py) → car<Id><Parça>
    ...vehicles.flatMap((_vehicle) => ['chassis', 'wheel', 'antena', 'backLightsBrake', 'backLightsReverse'].map((_part) => ({
        name: `car${capitalize(_vehicle.id)}${capitalize(_part)}`,
        source: `./models/car/${_vehicle.id}/${_part}.glb`
    }))),

    // Harf seti (tools/blender/glyphs.py) — BLOCK_TEXTS'te geçen karakterler
    ...GLYPH_CODES.flatMap((_code) => [
        { name: `glyph${_code}Base`, source: `./models/glyphs/u${_code}/base.glb` },
        { name: `glyph${_code}Collision`, source: `./models/glyphs/u${_code}/collision.glb` }
    ]),

    // folio-2019'dan kalan dünya parçaları (sırayla kendi modellerinle değiştirilecek)
    { name: 'introArrowKeyBase', source: './models/intro/arrowKey/base.glb' },
    { name: 'introArrowKeyCollision', source: './models/intro/arrowKey/collision.glb' },
    { name: 'crossroadsStaticBase', source: './models/crossroads/static/base.glb' },
    { name: 'crossroadsStaticCollision', source: './models/crossroads/static/collision.glb' },
    { name: 'crossroadsStaticFloorShadow', source: './models/crossroads/static/floorShadow.png', type: 'texture' },
    { name: 'projectsBoardStructure', source: './models/projects/board/structure.glb' },
    { name: 'projectsBoardCollision', source: './models/projects/board/collision.glb' },
    { name: 'projectsBoardStructureFloorShadow', source: './models/projects/board/floorShadow.png', type: 'texture' },
    { name: 'projectsBoardPlane', source: './models/projects/board/plane.glb' },
    { name: 'playgroundStaticBase', source: './models/playground/static/base.glb' },
    { name: 'playgroundStaticCollision', source: './models/playground/static/collision.glb' },
    { name: 'playgroundStaticFloorShadow', source: './models/playground/static/floorShadow.png', type: 'texture' },
    { name: 'brickBase', source: './models/brick/base.glb' },
    { name: 'brickCollision', source: './models/brick/collision.glb' },
    { name: 'hornBase', source: './models/horn/base.glb' },
    { name: 'hornCollision', source: './models/horn/collision.glb' },
    { name: 'bowlingBallBase', source: './models/bowlingBall/base.glb' },
    { name: 'bowlingBallCollision', source: './models/bowlingBall/collision.glb' },
    { name: 'bowlingPinBase', source: './models/bowlingPin/base.glb' },
    { name: 'bowlingPinCollision', source: './models/bowlingPin/collision.glb' },
    { name: 'areaKeyEnter', source: './models/area/keyEnter.png', type: 'texture' },
    { name: 'areaEnter', source: './models/area/enter.png', type: 'texture' },
    { name: 'tilesABase', source: './models/tiles/a/base.glb' },
    { name: 'tilesACollision', source: './models/tiles/a/collision.glb' },
    { name: 'tilesBBase', source: './models/tiles/b/base.glb' },
    { name: 'tilesBCollision', source: './models/tiles/b/collision.glb' },
    { name: 'tilesCBase', source: './models/tiles/c/base.glb' },
    { name: 'tilesCCollision', source: './models/tiles/c/collision.glb' },
    { name: 'tilesDBase', source: './models/tiles/d/base.glb' },
    { name: 'tilesDCollision', source: './models/tiles/d/collision.glb' },
    { name: 'tilesEBase', source: './models/tiles/e/base.glb' },
    { name: 'tilesECollision', source: './models/tiles/e/collision.glb' }
]
